import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { CalloutType, FeatureCallout, FeatureCalloutAction } from '../../feature-callout/feature-callout.model';
import { FeatureCalloutService } from '../../feature-callout/feature-callout.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { UserProfileService } from '../../shared/instrumentation/user-profile.service';
import { ObservableScope } from '../../shared/models/observable-scope.enum';
import { ConsumerSkuType } from '../../shared/services/consumer-license.service';
import { DeviceDetector } from '../../shared/services/device-detector.service';
import { DocumentService } from '../../shared/services/document.service';
import { EventService } from '../../shared/services/event.service';
import { FloodgateService } from '../../shared/services/floodgate.service';
import { PerformanceService } from '../../shared/services/performance.service';
import { ShellShimService } from '../../shared/services/shell-shim.service';
import { WindowService } from '../../shared/services/window.service';
import { AngularEvents } from '../../shared/shared-context/angular-events';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { LocService } from '../../shared/shared-context/loc.service';
import { ConsumerUserConsentGroup, UserConsentConfig, UserConsentConfigService } from '../../shared/shared-context/user-consent-config.service';
import { UserContextService } from '../../shared/shared-context/user-context.service';
import { HtmlEscaper } from '../../shared/utilities/html-escaper.utility';
import { OneDriveUploadService } from '../../upload/upload.service';
import { FirstRunConfigService } from '../first-run-component/first-run-config.service';
import { FirstRunData, FirstRunDataService } from '../shared/first-run-data.service';
import { FirstRunCalloutsConfigService } from './first-run-callouts-config.service';

@Injectable({
    providedIn: 'root'
})
export class FirstRunCalloutsService {
    private static readonly minWidthForCalloutShell = 1024;
    private firstRunVersion: number;
    private readonly installButtonResponsePromise: Promise<string>;
    private readonly modalFreClosedPromise: Promise<void>;
    private readonly uploadButtonResponsePromise: Promise<void>;
    private readonly appBarShowingPromise: Promise<void>;
    private readonly consumerSubscriptionStatusPromise: Promise<ConsumerSkuType>;
    private readonly userConsentPromise: Promise<UserConsentConfig>;
    private readonly disabled: boolean;
    private readonly leftNavShowing: boolean;
    private readonly isCommercialUser: boolean;
    private isTrialEligible: boolean;
    private initialized = false;
    private showModalFre: boolean;
    private paidConsumerModalShown: boolean;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly contextService: ContextService,
        private readonly locService: LocService,
        private readonly eventService: EventService,
        private readonly firstRunCalloutsConfigService: FirstRunCalloutsConfigService,
        private readonly firstRunConfigService: FirstRunConfigService,
        private readonly firstRunDataService: FirstRunDataService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly userContextService: UserContextService,
        private readonly featureCalloutService: FeatureCalloutService,
        private readonly floodgateService: FloodgateService,
        deviceDetector: DeviceDetector,
        private readonly performanceService: PerformanceService,
        private readonly windowService: WindowService,
        private readonly documentService: DocumentService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly instrumentationService: InstrumentationService,
        private readonly userProfileService: UserProfileService,
        private readonly uploadService: OneDriveUploadService,
        private readonly shellShimService: ShellShimService,
        private readonly userConsentService: UserConsentConfigService
    ) {
        this.disabled = !deviceDetector.isDesktopDevice() || !firstRunCalloutsConfigService.config || firstRunCalloutsConfigService.config.hideInTestMode;
        if (this.disabled) {
            return;
        }

        this.installButtonResponsePromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.installButtonResponse),
            take(1))
            .toPromise()
            .then(event => {
                if (event.args && event.args.length) {
                    return Promise.resolve(event.args[0]);
                }
            });

        this.paidConsumerModalShown = false;
        this.consumerSubscriptionStatusPromise = this.consumerInstallConfigService.getConfig().then(config => config && config.currentSkuInfo && config.currentSkuInfo.skuType);
        if (this.startupConfigService.config.authType === AuthType.MSA) {
            this.consumerSubscriptionStatusPromise.then(status => {
                this.paidConsumerModalShown = !(status === ConsumerSkuType.NotSubscribed || status === ConsumerSkuType.NonActive || status === ConsumerSkuType.NotDetected);
            });
        }
        this.isCommercialUser = this.startupConfigService.config.authType === AuthType.OrgId;
        this.userConsentPromise = !this.isCommercialUser ? this.userConsentService.getConfig() : Promise.resolve(null);

        this.isTrialEligible = false;
        this.modalFreClosedPromise = this.firstRunConfigService.getConfig().then(firstRunConfig => {
            this.isTrialEligible = !!firstRunConfig?.model?.isTrialEligible;
            this.showModalFre = !!firstRunConfig && !!firstRunConfig.model && !!firstRunConfig.model.slides;
            if (this.showModalFre) {
                return eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === AngularEvents.firstRunModalClosed),
                    take(1))
                    .toPromise()
                    .then(() => Promise.resolve());
            } else {
                return Promise.resolve();
            }
        });

        this.uploadButtonResponsePromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.uploadButtonResponse),
            take(1))
            .toPromise()
            .then(() => Promise.resolve());

        if (this.capabilitiesService.capabilities.appBarEnabled) {
            this.appBarShowingPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.appBarReady),
                take(1))
                .toPromise()
                .then(() => Promise.resolve());
        } else {
            this.appBarShowingPromise = Promise.resolve();
        }

        this.leftNavShowing = this.capabilitiesService.capabilities.leftNavReact;
    }

    public initializeFirstRun(): void {
        // The shell is different for mobile/tablet scenarios and first run is less relevant
        if (this.disabled || this.initialized) {
            return;
        }

        this.initialized = true;

        this.firstRunVersion = this.firstRunCalloutsConfigService.config.version;
        this.fetchFirstRun()
            .then(firstRunState => {
                // Show callouts when available
                const firstRun = firstRunState.CompletedFirstRun ? Promise.resolve(new Array<FeatureCallout>()) : this.getFirstRunCallouts().then(callouts => {
                    if (!callouts.length) {
                        return callouts;
                    }

                    this.initCalloutActions(callouts);

                    const startPoint = firstRunState.Version !== this.firstRunVersion ? 0 :
                        typeof (firstRunState.FurthestCompleted) !== 'undefined' &&
                            firstRunState.FurthestCompleted < callouts.length
                            ? firstRunState.FurthestCompleted
                            : firstRunState.StepsCompleted < callouts.length ?
                                firstRunState.StepsCompleted : 0;

                    for (let i = 0; i < startPoint; i++) {
                        callouts[i].completed = true;
                    }

                    this.performanceService.mark('FRE_Ready');

                    const firstCallout = callouts[startPoint];
                    this.firstRunDataService.updateFirstRunData(
                        {CompletedFirstRun: false, StepsCompleted: startPoint + 1, Version: this.firstRunVersion, TotalSteps: callouts.length}, FirstRunDataService.firstRunDataKey,
                        firstCallout.calloutType);
                    return callouts;
                });

                // Wait for both to return so the first run callouts get shown first
                firstRun.then(callouts => {
                    if (callouts.length) {
                        this.featureCalloutService.addCallouts(callouts);
                    } else {
                        // Callouts that show after the first run
                        this.getAfterFirstRunCallouts().then(afterFirstRunCallouts => {
                            if (afterFirstRunCallouts.length) {
                                this.featureCalloutService.addCallouts(afterFirstRunCallouts);
                            }
                        });
                    }
                });
            });
    }

    public fetchFirstRun(): Promise<FirstRunData> {
        return this.firstRunDataService.fetchFirstRunData(this.firstRunVersion, false, FirstRunDataService.firstRunDataKey);
    }

    /**
     * Returns a promise with an array of the callouts that should be shown to the user on first run.
     */
    private getFirstRunCallouts(): Promise<Array<FeatureCallout>> {
        const isEduUser = this.userContextService.user.isEdu;
        const calloutsConfig = this.firstRunCalloutsConfigService.config;
        if (this.capabilitiesService.capabilities.freNewCalloutsEnabled) {
            return new Promise<Array<FeatureCallout>>((resolve, reject) => {
                Promise.all([this.shellShimService.afterShellPlusLoaded, this.modalFreClosedPromise, this.consumerSubscriptionStatusPromise, this.userConsentPromise, this.installButtonResponsePromise, this.appBarShowingPromise])
                    .then(([shellResult, _, consumerSubscriptionStatus, consentConfig]) => {
                        const callouts = new Array<FeatureCallout>();
                        const includeShellCallouts = shellResult;
                        const appLauncher = this.documentService.nativeDocument.getElementById('O365_MainLink_NavMenu');
                        if (includeShellCallouts && appLauncher) {
                            const findMoreAppsSvg = !this.isCommercialUser ? calloutsConfig.findMoreAppsConsumerSvg : isEduUser ? calloutsConfig.findMoreAppsEduSvg : calloutsConfig.findMoreAppsCommercialSvg;
                            callouts.push(new FeatureCallout(
                                appLauncher,
                                CalloutType.AppLauncher,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunFindMoreAppsTitle,
                                this.isCommercialUser ? this.locService.FirstRunFindMoreAppsSubtitleCommercial : this.locService.FirstRunFindMoreAppsSubtitleConsumer,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, null, findMoreAppsSvg));
                            if (this.userContextService.user.isAdmin) {
                                if (appLauncher) {
                                    callouts.push(new FeatureCallout(
                                        appLauncher,
                                        CalloutType.AppLauncher,
                                        KnownAreas.FirstRun,
                                        isEduUser ? this.locService.FirstRunManageSchoolTitle : this.locService.FirstRunManageOrganizationTitle,
                                        this.locService.FirstRunManageOrganizationSubtitle,
                                        null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.adminCenterSvg));
                                }
                            }
                        }
                        const createNewButton = this.documentService.nativeDocument.getElementById('83D3F491-B586-4266-8738-89776471BF21'); // Static ids assigned to tiles on app bar
                        if (createNewButton) {
                            callouts.push(new FeatureCallout(
                                createNewButton,
                                CalloutType.NewButton,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunCreateHereTitle,
                                this.locService.FirstRunCreateHereSubtitle,
                                null, null, null, null, null, true, null, null, null, null, null, null, null, null, calloutsConfig.createHereSvg));
                        }
                        const myContentButton = this.documentService.nativeDocument.getElementById('F57D5D29-4967-495C-A648-F7AB66BF8572');
                        if (myContentButton && this.isCommercialUser) {
                            callouts.push(new FeatureCallout(
                                myContentButton,
                                CalloutType.MyContent,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunManageContentTitle,
                                this.locService.FirstRunManageContentSubtitle,
                                null, null, null, null, null, true, null, null, null, null, null, null, null, null, calloutsConfig.manageContentSvg));
                        }
                        const uploadButton = this.isCommercialUser ?
                            this.documentService.nativeDocument.getElementById('upload-button-QuickAccess') :
                            this.documentService.nativeDocument.getElementById('upload-button');
                        if (uploadButton) {
                            callouts.push(new FeatureCallout(
                                uploadButton,
                                CalloutType.Upload,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunUploadExistingContentTitle,
                                this.locService.FirstRunUploadExistingContentSubtitle,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.uploadContentSvg));
                        }
                        if (!this.isCommercialUser && consumerSubscriptionStatus === ConsumerSkuType.NonActive || consumerSubscriptionStatus === ConsumerSkuType.NotSubscribed &&
                            (!consentConfig || consentConfig.userConsent === ConsumerUserConsentGroup.CanConsent)) {
                            const consumerBuyButton = this.documentService.nativeDocument.getElementById('consumer-buy-button');
                            if (consumerBuyButton) {
                                callouts.push(new FeatureCallout(
                                    consumerBuyButton,
                                    CalloutType.BuyOffice,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunUpgradeAnyTimeTitle,
                                    this.locService.FirstRunUpgradeAnyTimeSubtitle,
                                    null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.upgradeSvg));
                            }
                        }
                        const installButton = this.isCommercialUser ? this.documentService.nativeDocument.getElementById('install-dropdown-link') : this.documentService.nativeDocument.getElementById('installbutton-MSA-RedirectToMyAccountPage');
                        if (installButton) {
                            callouts.push(new FeatureCallout(
                                installButton,
                                CalloutType.Install,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunDownloadAppsTitle,
                                this.locService.FirstRunDownloadAppsSubtitle,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.officeMobileAppSvg));
                        }

                        const feedbackButton = this.documentService.nativeDocument.getElementById('feedbackContainer');
                        if (feedbackButton) {
                            callouts.push(new FeatureCallout(
                                feedbackButton,
                                CalloutType.Feedback,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunYoureAllSetTitle,
                                this.locService.FirstRunYoureAllSetSubtitle,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.allSetSvg, true));
                        }
                        this.userProfileService.incrementMetric(KnownInstrumentationEvents.FreNewCalloutsImpression);
                        const params: InstrumentationParams = {};
                        params[InstrumentationParam.FirstRunTotalSteps] = callouts.length.toString();
                        params[InstrumentationParam.State] = 'first-run';
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FreNewCalloutsEvent, params);

                        resolve(callouts);
                    })
                    .catch(error => {
                        // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                        this.globalErrorHandler.handleError(error, ErrorId.LAtlv);

                        reject();
                    });
            });
        }
        return new Promise<Array<FeatureCallout>>((resolve, reject) => {
            Promise.all([this.shellShimService.afterShellPlusLoaded, this.installButtonResponsePromise, this.modalFreClosedPromise, this.consumerSubscriptionStatusPromise, this.userConsentPromise])
                .then(([shellResult, installResult, _, consumerSubscriptionStatus, consentConfig /* Can be null for OrgId users */]) => {
                    const callouts = new Array<FeatureCallout>();
                    const showEduFrePageTourV2 = this.capabilitiesService.capabilities.eduPageTourFreEnabled && this.userContextService.user.isEdu;
                    if (this.capabilitiesService.capabilities.pwaCommercialNonEduFreEnabled && this.capabilitiesService.capabilities.pwaEnabled) {
                        const searchBox = this.documentService.nativeDocument.querySelector('.fast-hero-search');
                        if (searchBox) {
                            callouts.push(new FeatureCallout(
                                searchBox,
                                CalloutType.SearchBar,
                                KnownAreas.FirstRun,
                                HtmlEscaper.stripTags(this.locService.FirstRunCommercialNonEduSearchBoxMessagePwa),
                                ''));
                        }
                        const createNewButton = this.documentService.nativeDocument.getElementById('create-menu-dropdown-link');
                        if (createNewButton) {
                            callouts.push(new FeatureCallout(
                                createNewButton,
                                CalloutType.NewButton,
                                KnownAreas.FirstRun,
                                HtmlEscaper.stripTags(this.locService.FirstRunCommercialNonEduCreateNewMessagePwa),
                                ''));
                        }
                        const recommended = this.documentService.nativeDocument.querySelector('.list-header__title--carousel');
                        if (recommended) {
                            callouts.push(new FeatureCallout(
                                recommended,
                                CalloutType.Recommended,
                                KnownAreas.FirstRun,
                                HtmlEscaper.stripTags(this.locService.FirstRunCommercialNonEduRecommendedMessagePwa),
                                ''));
                        }
                        const sharedWithMe = this.documentService.nativeDocument.getElementById('tab-list-header-link-shared-mru');
                        if (sharedWithMe) {
                            callouts.push(new FeatureCallout(
                                sharedWithMe,
                                CalloutType.SharedTab,
                                KnownAreas.FirstRun,
                                HtmlEscaper.stripTags(this.locService.FirstRunCommercialNonEduSharedWithMeMessagePwa),
                                ''));
                        }
                        resolve(callouts);
                        return;
                    }

                    if (this.capabilitiesService.capabilities.pwaFreeConsumerFreEnabled && this.capabilitiesService.capabilities.pwaEnabled &&
                        (consumerSubscriptionStatus === ConsumerSkuType.NonActive || consumerSubscriptionStatus === ConsumerSkuType.NotSubscribed)) {
                        const createNewButton = this.documentService.nativeDocument.getElementById('create-menu-dropdown-link');
                        if (createNewButton) {
                            callouts.push(new FeatureCallout(
                                createNewButton,
                                CalloutType.NewButton,
                                KnownAreas.FirstRun,
                                HtmlEscaper.stripTags(this.locService.FirstRunConsumerCreateNewCalloutPwa),
                                ''));
                        }
                        const oneDriveAppTile = this.documentService.nativeDocument.getElementById('ShellDocuments_link');
                        if (oneDriveAppTile && this.isElementInViewport(oneDriveAppTile)) {
                            callouts.push(new FeatureCallout(
                                oneDriveAppTile,
                                CalloutType.OneDriveTile,
                                KnownAreas.FirstRun,
                                HtmlEscaper.stripTags(this.locService.FirstRunConsumerOneDriveCalloutPwa),
                                '',
                                ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                        }
                        const consumerBuyButton = this.documentService.nativeDocument.getElementById('consumer-buy-button');
                        const firstRunConsumerBuyOfficeCalloutPwa = this.capabilitiesService.capabilities.altaBrandingChangesEnabled ? (this.isTrialEligible ? this.locService.FirstRunConsumerTryMicrosoft365CalloutTitle : this.locService.FirstRunConsumerBuyOfficeCalloutPwaAlta) : this.locService.FirstRunConsumerBuyOfficeCalloutPwa;
                        const firstRunConsumerTryMicrosoft365CalloutMessage = this.capabilitiesService.capabilities.altaBrandingChangesEnabled && this.isTrialEligible ?  this.locService.FirstRunConsumerTryMicrosoft365CalloutMessage : '';
                        const firstRunConsumerTryMicrosoft365CalloutLegalText = this.capabilitiesService.capabilities.altaBrandingChangesEnabled && this.isTrialEligible ?  this.locService.FirstRunConsumerTryMicrosoft365CalloutLegalText : '';

                        if (consumerBuyButton && (!consentConfig || consentConfig.userConsent === ConsumerUserConsentGroup.CanConsent)) {
                            callouts.push(new FeatureCallout(
                                consumerBuyButton,
                                CalloutType.BuyOffice,
                                KnownAreas.FirstRun,
                                HtmlEscaper.stripTags(firstRunConsumerBuyOfficeCalloutPwa),
                                HtmlEscaper.stripTags(firstRunConsumerTryMicrosoft365CalloutMessage),
                                ['generic-button--fluent--secondary', 'ms-bgc-w', 'ms-fcl-tp', 'box-shadow--major'],
                                ['generic-button--fluent--primary', 'ms-bgc-tp', 'ms-fcl-w'],
                                null,
                                false,
                                false,
                                false,
                                '',
                                false,
                                null,
                                null,
                                null,
                                HtmlEscaper.stripTags(firstRunConsumerTryMicrosoft365CalloutLegalText)));
                        }
                    }

                    if (this.userContextService.user.isEdu) {
                        if (this.userContextService.user.isEduFaculty) {
                            if (!showEduFrePageTourV2) {
                                const formsAppTile = this.documentService.nativeDocument.getElementById('ShellOfficeForms_link');
                                if (formsAppTile && this.isElementInViewport(formsAppTile)) {
                                    callouts.push(new FeatureCallout(
                                        formsAppTile,
                                        CalloutType.FormsTile,
                                        KnownAreas.FirstRun,
                                        HtmlEscaper.stripTags(this.locService.FirstRunFacultyFormsMessageUpdated),
                                        '',
                                        ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                    }
                                const oneNoteAppTile = this.documentService.nativeDocument.getElementById('ShellOneNoteOnline_link');
                                if (oneNoteAppTile && this.isElementInViewport(oneNoteAppTile)) {
                                    callouts.push(new FeatureCallout(
                                        oneNoteAppTile,
                                        CalloutType.OneNoteTile,
                                        KnownAreas.FirstRun,
                                        HtmlEscaper.stripTags(this.locService.FirstRunFacultyOneNoteMessageUpdated),
                                        '',
                                        ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                    }
                                const oneDriveAppTile = this.documentService.nativeDocument.getElementById('ShellDocuments_link');
                                if (oneDriveAppTile && this.isElementInViewport(oneDriveAppTile)) {
                                    callouts.push(new FeatureCallout(
                                        oneDriveAppTile,
                                        CalloutType.OneDriveTile,
                                        KnownAreas.FirstRun,
                                        HtmlEscaper.stripTags(this.locService.FirstRunEduOneDriveMessageUpdated),
                                        '',
                                        ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                    }
                                resolve(callouts);
                                return;
                            } else { // Version 2 of page tour FRE and is Edu Faculty
                                /*
                                // Removing create new callout until provisioning issues with new users is resolved
                                const createNewButton = this.documentService.nativeDocument.getElementById('create-menu-dropdown-link');
                                if (createNewButton) {
                                    callouts.push(new FeatureCallout(
                                        createNewButton,
                                        CalloutType.NewButton,
                                        KnownAreas.FirstRun,
                                        HtmlEscaper.stripTags(this.locService.FirstRunEduPageTourV2CreateNewCallout),
                                        '',
                                        ['workload-item--fluent--hover-style', 'ms-bgc-w']));
                                    }
                                */
                                const teamsTile = this.documentService.nativeDocument.getElementById('ShellSkypeTeams_link');
                                if (teamsTile && this.isElementInViewport(teamsTile)) {
                                    callouts.push(new FeatureCallout(
                                        teamsTile,
                                        CalloutType.TeamsTile,
                                        KnownAreas.FirstRun,
                                        HtmlEscaper.stripTags(this.locService.FirstRunEduFacultyPageTourV2TeamsCallout),
                                        '',
                                        ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                    }
                                resolve(callouts);
                                return;
                            }
                        } else if (this.userContextService.user.isEduStudent && showEduFrePageTourV2) {
                            // only show callouts for edu student if we are on page tour flight
                            /*
                            // Removing create new callout until provisioning issues with new users is resolved
                            const createNewButton = this.documentService.nativeDocument.getElementById('create-menu-dropdown-link');
                            if (createNewButton) {
                                callouts.push(new FeatureCallout(
                                    createNewButton,
                                    CalloutType.NewButton,
                                    KnownAreas.FirstRun,
                                    HtmlEscaper.stripTags(this.locService.FirstRunEduPageTourV2CreateNewCallout),
                                    '',
                                    ['workload-item--fluent--hover-style', 'ms-bgc-w']));
                            }
                            */
                            const teamsTile = this.documentService.nativeDocument.getElementById('ShellSkypeTeams_link');
                            if (teamsTile && this.isElementInViewport(teamsTile)) {
                                callouts.push(new FeatureCallout(
                                    teamsTile,
                                    CalloutType.TeamsTile,
                                    KnownAreas.FirstRun,
                                    HtmlEscaper.stripTags(this.locService.FirstRunEduStudentPageTourV2TeamsCallout),
                                    '',
                                    ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                }
                            resolve(callouts);
                            return;
                        }
                    }

                    if ((this.isCommercialUser || this.paidConsumerModalShown) && installResult && installResult.length) {
                        const installButton = this.documentService.nativeDocument.querySelector('.install-button');
                        if (installButton && !this.capabilitiesService.capabilities.pwaEnabled) {
                            callouts.push(new FeatureCallout(
                                installButton,
                                CalloutType.Install,
                                KnownAreas.FirstRun,
                                installResult,
                                this.paidConsumerModalShown
                                    ? this.capabilitiesService.capabilities.altaBrandingChangesEnabled ? this.locService.PageTourGetOfficeMessageAlta : this.locService.PageTourGetOfficeMessage
                                    : this.locService.FirstRunInstallSubtitle,
                                ['generic-button--fluent--primary', 'ms-bgc-tp', 'ms-fcl-w', 'box-shadow--major'],
                                ['generic-button--fluent--secondary', 'ms-bgc-w', 'ms-fcl-tp']));
                        }

                        if (this.paidConsumerModalShown && !this.capabilitiesService.capabilities.pwaEnabled) {
                            const oneDriveAppTile = this.documentService.nativeDocument.getElementById('ShellDocuments_link');
                            if (oneDriveAppTile && this.isElementInViewport(oneDriveAppTile)) {
                                callouts.push(new FeatureCallout(
                                    oneDriveAppTile,
                                    CalloutType.OneDriveTile,
                                    KnownAreas.FirstRun,
                                    this.locService.PageTourNewDocumentTitle,
                                    this.locService.PageTourNewDocumentMessage,
                                    ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                }

                            const myAccountLink = this.documentService.nativeDocument.querySelector('.my-account__link');
                            if (myAccountLink) {
                                callouts.push(new FeatureCallout(
                                    myAccountLink,
                                    CalloutType.MyAccount,
                                    KnownAreas.FirstRun,
                                    this.locService.PageTourMyAccountTitle,
                                    this.locService.PageTourMyAccountMessage,
                                    ['my-account__link--hover-style']));
                            }
                        }

                        if (this.paidConsumerModalShown && this.capabilitiesService.capabilities.pwaEnabled) {
                            const wordAppTile = this.documentService.nativeDocument.getElementById('ShellWordOnline_link');
                            if (wordAppTile && this.isElementInViewport(wordAppTile)) {
                                callouts.push(new FeatureCallout(
                                    wordAppTile,
                                    CalloutType.WordTile,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunPaidConsumerCallout1Title,
                                    this.locService.FirstRunPaidConsumerCallout1Subtitle,
                                    ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                }

                            const oneDriveAppTile = this.documentService.nativeDocument.getElementById('ShellDocuments_link');
                            if (oneDriveAppTile && this.isElementInViewport(oneDriveAppTile)) {
                                callouts.push(new FeatureCallout(
                                    oneDriveAppTile,
                                    CalloutType.OneDriveTile,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunPaidConsumerCallout2Title,
                                    this.locService.FirstRunPaidConsumerCallout2Subtitle,
                                    ['workload-item--fluent--hover-style', 'ms-bgc-w'], null, null, null, null, this.leftNavShowing));
                                }

                            const myAccountLink = this.documentService.nativeDocument.querySelector('.my-account__link');
                            if (myAccountLink) {
                                callouts.push(new FeatureCallout(
                                    myAccountLink,
                                    CalloutType.MyAccount,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunPaidConsumerCallout3Title,
                                    this.locService.FirstRunPaidConsumerCallout3Subtitle,
                                    ['my-account__link--hover-style']));
                            }
                        }

                        if (this.showModalFre || this.paidConsumerModalShown) {
                            // we only want callout specific to our modal FREs if we have modal fre callouts enabled
                            resolve(callouts);
                            return;
                        }
                    }

                    const includeShellCallouts = shellResult;
                    const windowWidth = this.windowService.innerWidth;
                    const appLauncher = this.documentService.nativeDocument.getElementById('O365_MainLink_NavMenu');
                    if (includeShellCallouts &&
                        appLauncher &&
                        windowWidth > FirstRunCalloutsService.minWidthForCalloutShell) {
                        const subTitle = this.capabilitiesService.capabilities.altaBrandingChangesEnabled ? this.locService.FirstRunAppLauncherAltaSubtitle : this.locService.FirstRunAppLauncherSubtitle;
                        callouts.push(new FeatureCallout(
                            appLauncher,
                            CalloutType.AppLauncher,
                            KnownAreas.FirstRun,
                            this.locService.FirstRunAppLauncherTitle,
                            subTitle));
                    }

                    if (this.userContextService.user.isAdmin) {
                        const adminTile = this.documentService.nativeDocument.getElementById('ShellAdmin_link');
                        if (adminTile && this.isElementInViewport(adminTile)) {
                            callouts
                                .push(new FeatureCallout(
                                    adminTile,
                                    CalloutType.AdminTile,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunAdminTitle,
                                    this.locService.FirstRunAdminSubtitle, null, null, null, null, null, this.leftNavShowing));
                        }
                    }

                    resolve(callouts);
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.k1Drr);

                    reject();
                });
        });
    }

    /**
     * Returns a promise with an array of the callouts that should be shown to the user.
     * These will be shown after the first run callouts and can be shown after the first run experience has been completed
     * TODO: Decouple these callouts from the first run experience.
     */
    private getAfterFirstRunCallouts(): Promise<Array<FeatureCallout>> {
        const promises: Array<Promise<any>> = [this.shellShimService.afterShellPlusLoaded, this.modalFreClosedPromise];
        const newCalloutsSeen = this.userProfileService.doesMetricExist(KnownInstrumentationEvents.FreNewCalloutsImpression);
        if (this.capabilitiesService.capabilities.freNewCalloutsEnabled) {
            promises.push(this.consumerSubscriptionStatusPromise, this.userConsentPromise, newCalloutsSeen, this.appBarShowingPromise);
        }
        return new Promise<Array<FeatureCallout>>((resolve, reject) => {
            Promise.all(promises)
                .then(([shellResult, _, consumerSubscriptionStatus, consentConfig, calloutsSeen]) => {
                    const calloutsPromises = new Array<Promise<FeatureCallout>>();
                    if (this.capabilitiesService.capabilities.freNewCalloutsEnabled && (!calloutsSeen || this.contextService.sharedContext.inMockMode)) { // haven't seen the new first run callouts, should see returning user callouts
                        const calloutsConfig = this.firstRunCalloutsConfigService.config;
                        const appLauncher = this.documentService.nativeDocument.getElementById('O365_MainLink_NavMenu'); // TODO: Without a callout element the other callouts are failing
                        calloutsPromises.push(Promise.resolve(
                            new FeatureCallout(
                                appLauncher,
                                CalloutType.Welcome,
                                this.contextService.isEdgeworth ? KnownAreas.Edgeworth : KnownAreas.Documents,
                                this.locService.FirstRunWelcomeBackTitle,
                                this.locService.FirstRunWelcomeBackSubtitle,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.welcomeBackSvg, false, true, KnownInstrumentationEvents.ReturningUserCalloutsImpression)
                        ));
                        const includeShellCallouts = shellResult;
                        if (includeShellCallouts && appLauncher) {
                            const isEduUser = this.userContextService.user.isEdu;
                            const findMoreAppsSvg = !this.isCommercialUser ? calloutsConfig.findMoreAppsConsumerSvg : isEduUser ? calloutsConfig.findMoreAppsEduSvg : calloutsConfig.findMoreAppsCommercialSvg;
                            calloutsPromises.push(Promise.resolve(
                                new FeatureCallout(
                                    appLauncher,
                                    CalloutType.AppLauncher,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunFindMoreAppsTitle,
                                    this.locService.FirstRunFindMoreAppsSubtitleReturningUser,
                                    null, null, null, null, null, false, null, null, null, null, null, null, null, null, findMoreAppsSvg, false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                            if (this.userContextService.user.isAdmin) {
                                if (appLauncher) {
                                    calloutsPromises.push(Promise.resolve(
                                        new FeatureCallout(
                                            appLauncher,
                                            CalloutType.AppLauncher,
                                            KnownAreas.FirstRun,
                                            this.locService.FirstRunAdminCenterMovedTitle,
                                            this.locService.FirstRunAdminCenterMovedSubtitle,
                                            null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.adminCenterSvg, false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                                }
                            }
                            const createNewButton = this.documentService.nativeDocument.getElementById('83D3F491-B586-4266-8738-89776471BF21'); // Static ids assigned to tiles on app bar
                            if (createNewButton) {
                                calloutsPromises.push(Promise.resolve(
                                    new FeatureCallout(
                                    createNewButton,
                                    CalloutType.NewButton,
                                    KnownAreas.FirstRun,
                                    this.locService.ExpandedCreateCapabilitiesTitle,
                                    this.locService.FirstRunExpandedCreateCapabilitiesSubittle,
                                    null, null, null, null, null, true, null, null, null, null, null, null, null, null, calloutsConfig.createHereSvg, false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                            }
                            const myContentButton = this.documentService.nativeDocument.getElementById('F57D5D29-4967-495C-A648-F7AB66BF8572');
                            if (myContentButton && this.isCommercialUser) {
                                calloutsPromises.push(Promise.resolve(new FeatureCallout(
                                    myContentButton,
                                    CalloutType.MyContent,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunManageContentTitle,
                                    this.locService.FirstRunManageContentSubtitle,
                                    null, null, null, null, null, true, null, null, null, null, null, null, null, null, calloutsConfig.manageContentSvg, false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                            }
                            if (!this.isCommercialUser && consumerSubscriptionStatus === ConsumerSkuType.NonActive || consumerSubscriptionStatus === ConsumerSkuType.NotSubscribed &&
                                (!consentConfig || consentConfig.userConsent === ConsumerUserConsentGroup.CanConsent)) {
                                const consumerBuyButton = this.documentService.nativeDocument.getElementById('installbutton-MSA-RedirectToMyAccountPage');
                                if (consumerBuyButton) {
                                    calloutsPromises.push(Promise.resolve(new FeatureCallout(
                                        consumerBuyButton,
                                        CalloutType.BuyOffice,
                                        KnownAreas.FirstRun,
                                        this.locService.FirstRunUpgradeAnyTimeTitle,
                                        this.locService.FirstRunUpgradeAnyTimeSubtitle,
                                        null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.upgradeSvg, false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                                }
                            }
                            const feedbackButton = this.documentService.nativeDocument.getElementById('feedbackContainer');
                            if (feedbackButton) {
                                calloutsPromises.push(Promise.resolve(new FeatureCallout(
                                    feedbackButton,
                                    CalloutType.Feedback,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunYoureAllSetTitle,
                                    this.locService.FirstRunYoureAllSetSubtitleReturningUser,
                                    null, null, null, null, null, false, null, null, null, null, null, null, null, null, calloutsConfig.allSetSvg, true, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                            }
                        }
                        const params: InstrumentationParams = {};
                        params[InstrumentationParam.FirstRunTotalSteps] = calloutsPromises.length.toString();
                        params[InstrumentationParam.State] = 'after-first-run';
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FreNewCalloutsEvent, params);

                        this.userProfileService.incrementMetric(KnownInstrumentationEvents.FreNewCalloutsImpression);
                    } else {
                        if (shellResult) {
                            // Use the help icon in mock mode since settings item doesn't exist
                            const elementId = this.contextService.sharedContext.inMockMode ? '#O365_MainLink_Help' : '#O365_MainLink_Settings';
                            // In some cases the shell has two settings buttons on first load, but we want the last one
                            // TODO: Work with shell team to remove hard coded ids and resolve the state where there are two settings buttons with the same id
                            const shellElements = this.documentService.nativeDocument.querySelectorAll(elementId);
                            const shellElement = shellElements && shellElements.length ? shellElements[shellElements.length - 1] : null;
                            if (this.capabilitiesService.capabilities.themingCalloutEnabled) {
                                calloutsPromises.push(this.createThemingCallout(shellElement, KnownInstrumentationEvents.PrideThemesCalloutImpression, this.capabilitiesService.capabilities.showThemingCallout, this.locService.ThemesCalloutTitle, this.locService.ThemesCalloutSubtitle, 'callout-sprite'));
                            }
                            if (this.capabilitiesService.capabilities.consumerThemesCalloutEnabled) {
                                calloutsPromises.push(this.createThemingCallout(shellElement, KnownInstrumentationEvents.ConsumerThemesCalloutImpression, this.contextService.sharedContext.inMockMode, this.locService.ConsumerThemesCalloutTitle, null, null));
                            }
                        }

                        if (this.firstRunCalloutsConfigService.config.dragAndDropCalloutEnabled) {
                            calloutsPromises.push(
                                this.userProfileService.doesMetricExist(KnownInstrumentationEvents.UploadButtonDragAndDropCalloutImpression, true)
                                    .then(value => value ? Promise.reject() : this.uploadService.uploadEnabledPromise)
                                    .then(uploadEnabled => uploadEnabled ? this.uploadButtonResponsePromise : Promise.reject())
                                    .then(() => {
                                        const uploadButton = this.documentService.nativeDocument.getElementById('upload-button');
                                        if (uploadButton) {
                                            const closeObservable = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                                                filter(event => event.name === AngularEvents.uploadButtonClick),
                                                take(1)
                                            );

                                            const callout: FeatureCallout = new FeatureCallout(
                                                uploadButton,
                                                CalloutType.DragAndDrop,
                                                this.contextService.isEdgeworth ? KnownAreas.Edgeworth : KnownAreas.Documents,
                                                this.locService.FirstRunDragAndDropCalloutTitle,
                                                this.locService.FirstRunDragAndDropCalloutSubtitle,
                                                ['generic-button--fluent--primary', 'ms-bgc-tp', 'ms-fcl-w', 'box-shadow--major'],
                                                ['generic-button--fluent--secondary', 'ms-bgc-w', 'ms-fcl-tp'],
                                                null,
                                                null,
                                                null,
                                                null,
                                                null,
                                                null,
                                                closeObservable);

                                            this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.UploadButtonDragAndDropCalloutImpression, {}, [KnownInstrumentationEvents.UploadButtonDragAndDropCalloutImpression]);

                                            return callout;
                                        } else {
                                            return null;
                                        }
                                    })
                                    .catch(() => null)
                            );
                        }
                    }
                    Promise.all(calloutsPromises).then(callouts => resolve(callouts.filter(callout => callout !== null)));
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.Cgjwv);

                    reject();
                });
        });
    }

    private isElementInViewport (element: Element) {
        const rect = element.getBoundingClientRect();
        return (rect.top + rect.height < this.windowService.nativeWindow.innerHeight);
    }

    private createThemingCallout(shellElement: Element, instrumentationEvent: KnownInstrumentationEvents, showCalloutOverride: boolean, calloutTitle: string, calloutSubtitle: string, spriteImage: string) {
        return this.userProfileService.doesMetricExist(instrumentationEvent, true).then(value => {
            if (!value || showCalloutOverride) {
                if (shellElement) {
                    const closeObservable = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                        filter(event => event.name === AngularEvents.flexPaneOpen),
                        take(1)
                    );

                    const callout = new FeatureCallout(
                        shellElement,
                        CalloutType.NewThemes,
                        KnownAreas.Shell,
                        calloutTitle,
                        calloutSubtitle,
                        null,
                        null,
                        'themes-callout',
                        null,
                        null,
                        null,
                        spriteImage,
                        true,
                        closeObservable
                    );

                    callout.onAction = async (action: FeatureCalloutAction) => {
                        if (action === FeatureCalloutAction.Teach) {
                            const shellShim = await this.shellShimService.getShellShim();
                            shellShim.FlexPane.OpenFlexPaneForProvider('Settings');
                        }
                    };

                    this.instrumentationService.logImpressionEvent(instrumentationEvent, {}, [instrumentationEvent]);

                    return callout;
                }
            }
            return null;
        });
    }

    /**
     * Adds appropriate actions to each callout in the first run experience.
     * Adds a Done button for last action that dismisses callout, adds Next and Back buttons to others to navigate a series of callouts.
     * @param callouts: The array of callouts that comprise the first run experience
     */
    private initCalloutActions(callouts: Array<FeatureCallout>) {
        const numCallouts = callouts.length;
        const registerButtonClick = (stepsCompleted: number) => {
            const nextCallout = callouts[stepsCompleted];
            if (nextCallout) {
                this.firstRunDataService.updateFirstRunData({CompletedFirstRun: false, StepsCompleted: stepsCompleted + 1, Version: this.firstRunVersion, TotalSteps: numCallouts}, FirstRunDataService.firstRunDataKey, nextCallout.calloutType);
            }
        };

        callouts.forEach((callout, index) => {
            callout.buttonIdPrefix = `first-run-step-${index}-${callout.calloutType.toLowerCase()}`;
            callout.onAction = index === (numCallouts - 1)
                ? (action: FeatureCalloutAction) => {
                    if (action === FeatureCalloutAction.None) {
                        return;
                    } else if (action === FeatureCalloutAction.Back) {
                        registerButtonClick(index - 1);
                    } else {
                        this.firstRunDataService
                            .updateFirstRunData({CompletedFirstRun: true, StepsCompleted: numCallouts, Version: this.firstRunVersion, TotalSteps: numCallouts}, FirstRunDataService.firstRunDataKey);
                        if (this.paidConsumerModalShown && this.capabilitiesService.capabilities.showFloodgate && this.capabilitiesService.capabilities.paidConsumerFreFloodgateEnabled) {
                            this.floodgateService.recordFloodgate(['SawPaidConsumerFirstRun']);
                        }
                    }
                }
                : (action: FeatureCalloutAction) => {
                    if (action === FeatureCalloutAction.None) {
                        return;
                    } else if (action === FeatureCalloutAction.Close) {
                        this.firstRunDataService.updateFirstRunData({ CompletedFirstRun: true, StepsCompleted: index + 1, Version: this.firstRunVersion, TotalSteps: numCallouts }, FirstRunDataService.firstRunDataKey);
                        if (this.paidConsumerModalShown && this.capabilitiesService.capabilities.showFloodgate && this.capabilitiesService.capabilities.paidConsumerFreFloodgateEnabled) {
                            this.floodgateService.recordFloodgate(['SawPaidConsumerFirstRun']);
                        }
                    } else {
                        registerButtonClick(action === FeatureCalloutAction.Next ? index + 1 : index - 1);
                    }
                };
        });
    }
}
